import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { useSystemProfile } from '../utils/systemProfile.js';
import { userKey } from '../utils/storageHelper.js';
import { callLLM } from './llmService';

const CHAT_HISTORY_KEY = userKey('qais_chat_history');
const MAX_SESSIONS = 30;

// ── Persist helpers ──
function saveSessions(sessions) {
  try { localStorage.setItem(CHAT_HISTORY_KEY, JSON.stringify(sessions)); } catch {}
}
function loadSessions() {
  try { return JSON.parse(localStorage.getItem(CHAT_HISTORY_KEY) || '[]'); } catch { return []; }
}
function newSession(firstMessage) {
  return {
    id: `chat_${Date.now()}`,
    title: firstMessage.slice(0, 45) + (firstMessage.length > 45 ? '...' : ''),
    messages: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

const SYSTEM_PROMPT = `You are an expert AI compliance consultant specializing in EU AI Act, ISO/IEC 42001, and NIST AI RMF.
You provide clear, practical, actionable guidance.
When asked about compliance requirements, always reference specific articles, clauses, or functions.
Keep answers concise but complete. Use Markdown formatting: **bold** for key terms, bullet or numbered lists for steps, and GitHub-flavored Markdown tables (| col | col |) when comparing requirements. Be direct.`;

const STARTERS = [
  'What are the key requirements of the EU AI Act for high-risk AI?',
  'How do I start ISO 42001 certification?',
  'What is the difference between High Risk and Limited Risk under EU AI Act?',
  'What does NIST AI RMF GOVERN function require?',
  'What documents do I need for EU AI Act compliance?',
  'How do I conduct an AI risk assessment?',
];

function formatTime(iso) {
  const d = new Date(iso);
  const now = new Date();
  const diffDays = Math.floor((now - d) / 86400000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// ── PDF export helpers ──
// Same html2canvas + jsPDF technique as CrossFrameworkMapper.jsx's downloadDocument(),
// adapted for a chat transcript instead of a single generated document.

// Escape user-typed text before it goes into the raw HTML string that html2canvas
// renders. Assistant content is trusted the same way the mapper trusts its own
// AI-generated docs (rendered via renderChatMarkdownPdf below, unescaped, to match
// that existing behavior) — but a user's own typed chat message is free-form input,
// and never escaping it before innerJS-string interpolation would let literal
// "<"/">" characters be interpreted as real HTML/DOM nodes inside the PDF render.
function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// Convert a block of Markdown-table lines into an HTML <table> (ported from
// CrossFrameworkMapper.jsx's renderMarkdownTable, same styling).
function renderMarkdownTablePdf(lines) {
  const parseRow = (line) =>
    line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((cell) => cell.trim());

  const rows = lines
    .map(parseRow)
    .filter((cells) => !cells.every((c) => /^:?-{2,}:?$/.test(c)));

  if (rows.length === 0) return '';

  const [headerRow, ...bodyRows] = rows;
  const headerHtml = `<tr>${headerRow
    .map((cell) => `<th style="border: 1px solid #cbd5e1; background: #e2e8f0; color: #0e7c7c; padding: 6px 10px; text-align: left; font-size: 12px;">${cell}</th>`)
    .join('')}</tr>`;

  const bodyHtml = bodyRows
    .map((cells) => `<tr>${cells
      .map((cell) => `<td style="border: 1px solid #cbd5e1; padding: 6px 10px; font-size: 12px; vertical-align: top;">${cell}</td>`)
      .join('')}</tr>`)
    .join('');

  return `<table style="border-collapse: collapse; width: 100%; margin: 8px 0 12px 0;">${headerHtml}${bodyHtml}</table>`;
}

// Render an assistant message's markdown (headings/bold/bullets/tables) as styled
// HTML for the PDF (ported from CrossFrameworkMapper.jsx's renderAiMarkdown).
function renderChatMarkdownPdf(text) {
  if (!text) return '';

  const lines = text.split('\n');
  const outLines = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const isTableRow = /^\s*\|.*\|\s*$/.test(line);
    const nextIsSeparator = i + 1 < lines.length && /^\s*\|?[\s:|-]*-{2,}[\s:|-]*\|?\s*$/.test(lines[i + 1]);
    if (isTableRow && nextIsSeparator) {
      const tableLines = [];
      while (i < lines.length && /^\s*\|.*\|\s*$/.test(lines[i])) {
        tableLines.push(lines[i]);
        i++;
      }
      outLines.push(renderMarkdownTablePdf(tableLines));
    } else {
      outLines.push(line);
      i++;
    }
  }
  const withTables = outLines.join('\n');

  return withTables
    .replace(/^[=\-_]{3,}\s*$/gm, '')
    .replace(/^#{3}\s+(.+)$/gm, '<div style="color: #0e7c7c; text-decoration: underline; font-weight: bold; font-size: 15px; margin: 12px 0 5px 0;">$1</div>')
    .replace(/^#{2}\s+(.+)$/gm, '<div style="color: #0e7c7c; text-decoration: underline; font-weight: bold; font-size: 16px; margin: 14px 0 6px 0;">$1</div>')
    .replace(/^#{1}\s+(.+)$/gm, '<div style="color: #0e7c7c; text-decoration: underline; font-weight: bold; font-size: 18px; margin: 16px 0 8px 0;">$1</div>')
    .replace(/^[•*-]\s+(.+)$/gm, '<div style="margin-left: 16px; margin-bottom: 3px;">• $1</div>')
    .replace(/\*\*(.+?)\*\*/g, '<span style="color: #0e7c7c; text-decoration: underline; font-weight: bold;">$1</span>')
    .replace(/\*([^*\n]+?)\*/g, '<span style="color: #0e7c7c; text-decoration: underline; font-weight: bold;">$1</span>')
    .replace(/(?<![\w_])_([^_\n]+)_(?![\w_])/g, '<em>$1</em>')
    .replace(/<\/div>\n+/g, '</div>')
    .replace(/<\/table>\n+/g, '</table>')
    .replace(/\n{2,}/g, '\n')
    .replace(/\n/g, '<br>')
    .replace(/<br>\s*(<table)/g, '$1')
    .replace(/(<\/table>)\s*<br>/g, '$1');
}

// Load the QAIS logo from /public as base64 for embedding (ported from
// CrossFrameworkMapper.jsx's loadImageAsBase64).
async function loadImageAsBase64Pdf(path) {
  try {
    const response = await fetch(path);
    const blob = await response.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    console.warn('Logo load failed:', e);
    return null;
  }
}

// Renders LLM markdown safely.
// remark-gfm parses tables/bold/lists from markdown syntax. Models frequently emit
// literal `<br>` inside table cells (Markdown tables can't contain real line breaks),
// so we allow a SANITIZED subset of raw HTML rather than none at all:
//   rehypeRaw    — parses raw HTML tags the model wrote (e.g. <br>) into real elements
//   rehypeSanitize — strips anything dangerous from that raw HTML using the default
//                    safe-list schema (same one GitHub's own Markdown renderer uses):
//                    <script>, on*/event-handler attributes, javascript: URLs, <iframe>,
//                    <style>, <form>, etc. are all stripped; <br>, tables, formatting
//                    tags, and links with http(s)/mailto hrefs are kept.
// This does NOT re-open the XSS surface — it's a curated allow-list, not "render any HTML."
function MarkdownMessage({ content }) {
  return (
    <div className="md-content">
      <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw, rehypeSanitize]}>
        {content}
      </ReactMarkdown>
    </div>
  );
}

export default function ComplianceChatbot() {
  const _profile = useSystemProfile(); // company name for PDF filename/footer, matches CrossFrameworkMapper.jsx
  const [sessions, setSessions]         = useState(() => loadSessions());
  const [activeId, setActiveId]         = useState(null);
  const [messages, setMessages]         = useState([]);
  const [input, setInput]               = useState('');
  const [loading, setLoading]           = useState(false);
  const [sidebarOpen, setSidebarOpen]   = useState(true);
  const [editingId, setEditingId]       = useState(null);
  const [editTitle, setEditTitle]       = useState('');
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const messagesEndRef                  = useRef(null);

      // Scroll window to top on initial mount (when navigating to AI Assistant page)
        useEffect(() => {
          window.scrollTo({ top: 0, behavior: 'auto' });
        }, []);

        useEffect(() => {
          if (messages.length === 0) return;
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, [messages]);

  const activeSession = sessions.find(s => s.id === activeId);

  const startNewChat = () => {
    setActiveId(null);
    setMessages([]);
    setInput('');
  };

  const openSession = (id) => {
    const s = sessions.find(s => s.id === id);
    if (s) { setActiveId(id); setMessages(s.messages); }
  };

  const deleteSession = (id, e) => {
    e.stopPropagation();
    const updated = sessions.filter(s => s.id !== id);
    setSessions(updated);
    saveSessions(updated);
    if (activeId === id) startNewChat();
  };

  const startRename = (id, title, e) => {
    e.stopPropagation();
    setEditingId(id);
    setEditTitle(title);
  };

  const saveRename = (id) => {
    const updated = sessions.map(s => s.id === id ? { ...s, title: editTitle } : s);
    setSessions(updated);
    saveSessions(updated);
    setEditingId(null);
  };

  const sendMessage = async (text) => {
    const userText = (text || input).trim();
    if (!userText || loading) return;
    setInput('');
    setLoading(true);

    const userMsg   = { role: 'user',      content: userText, ts: new Date().toISOString() };
    const newMsgs   = [...messages, userMsg];
    setMessages(newMsgs);

    // Create or update session
    let sessionId = activeId;
    let updatedSessions = [...sessions];

    if (!sessionId) {
      // New session
      const s = newSession(userText);
      sessionId = s.id;
      s.messages = newMsgs;
      updatedSessions = [s, ...sessions].slice(0, MAX_SESSIONS);
      setActiveId(sessionId);
    } else {
      updatedSessions = updatedSessions.map(s =>
        s.id === sessionId ? { ...s, messages: newMsgs, updatedAt: new Date().toISOString() } : s
      );
    }

    setSessions(updatedSessions);
    saveSessions(updatedSessions);

    try {
      const apiMessages = [
        { role: 'system', content: SYSTEM_PROMPT },
        ...newMsgs.slice(-12).map(m => ({ role: m.role, content: m.content }))
      ];

      const reply = await callLLM({ messages: apiMessages, max_tokens: 800 });
      const aiMsg = { role: 'assistant', content: reply, ts: new Date().toISOString() };
      const finalMsgs = [...newMsgs, aiMsg];
      setMessages(finalMsgs);

      const finalSessions = updatedSessions.map(s =>
        s.id === sessionId ? { ...s, messages: finalMsgs, updatedAt: new Date().toISOString() } : s
      );
      setSessions(finalSessions);
      saveSessions(finalSessions);
    } catch (err) {
      console.error('AI Assistant: LLM call failed:', err);
      // Auth errors need a different message than generic failures — "try again"
      // is misleading when the real fix is "log back in," not "retry the same request."
      const isAuthError = err?.code === 'NOT_AUTHENTICATED';
      const isQuotaError = err?.code === 'QUOTA_EXCEEDED';
      const errContent = isAuthError
        ? '🔒 Your session has expired. Please refresh the page and log in again to continue.'
        : isQuotaError
          ? `⏳ ${err.message}`
          : '❌ Sorry, I could not get a response. Please try again.';
      const errMsg = { role: 'assistant', content: errContent, ts: new Date().toISOString(), error: true };
      const finalMsgs = [...newMsgs, errMsg];
      setMessages(finalMsgs);
    }
    setLoading(false);
  };

  // ── Download the active conversation as a styled PDF ──
  // Same html2canvas + jsPDF page-slicing technique as CrossFrameworkMapper.jsx's
  // downloadDocument(), rendering the transcript instead of a single document.
  const downloadChatAsPDF = async () => {
    if (!activeSession || messages.length === 0) return;
    setPdfGenerating(true);
    await new Promise(resolve => setTimeout(resolve, 50));

    const logoBase64  = await loadImageAsBase64Pdf('/logo.png');
    const companyName = _profile.companyName || '';
    const sessionTitle = activeSession.title || 'AI Assistant Conversation';
    const headerTitle  = 'Quantum AI Shield · AI Compliance Assistant';
    const generatedAt  = new Date();

    const messagesHtml = messages.map(msg => {
      const time = new Date(msg.ts).toLocaleString();
      if (msg.role === 'user') {
        return `
          <div style="margin: 16px 0 8px;">
            <div style="font-size: 11px; font-weight: 700; color: #1e40af; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 4px;">You &nbsp;·&nbsp; ${time}</div>
            <div style="background: #eff6ff; border-left: 4px solid #1e40af; border-radius: 8px; padding: 10px 14px; font-size: 13px; color: #1e293b; line-height: 1.6; white-space: pre-wrap;">${escapeHtml(msg.content)}</div>
          </div>
        `;
      }
      return `
        <div style="margin: 8px 0 18px;">
          <div style="font-size: 11px; font-weight: 700; color: #0e7c7c; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 4px;">🤖 AI Compliance Assistant &nbsp;·&nbsp; ${time}</div>
          <div style="background: #f8fafc; border-left: 4px solid #0e7c7c; border-radius: 8px; padding: 12px 16px; font-size: 13px; color: #1e293b; line-height: 1.7;">${renderChatMarkdownPdf(msg.content)}</div>
        </div>
      `;
    }).join('');

    const htmlContent = `
      <div style="font-family: 'Inter', 'Helvetica', sans-serif; max-width: 800px; margin: 0 auto; padding: 20px; background: white; color: #1e293b; line-height: 1.6; font-size: 15px;">
        ${logoBase64 ? `<div style="text-align: center; margin-bottom: 8px;"><img src="${logoBase64}" style="max-width: 180px; max-height: 90px;" alt="QuantumAI Shield" /></div>` : ''}
        <h1 style="color: #0e7c7c; border-bottom: 3px solid #0e7c7c; padding-bottom: 10px; font-size: 24px; text-align: center;">${escapeHtml(sessionTitle)}</h1>
        <p style="text-align: center;"><strong>AI Compliance Assistant Conversation — Quantum AI Shield</strong><br>${generatedAt.toLocaleDateString()}${companyName ? ` · ${escapeHtml(companyName)}` : ''}</p>

        <h2 style="color: #0e7c7c; border-bottom: 2px solid #0e7c7c; font-size: 19px; margin-top: 22px;">Conversation</h2>
        ${messagesHtml}

        <h2 style="color: #0e7c7c; border-bottom: 2px solid #0e7c7c; font-size: 19px;">Disclaimer</h2>
        <p style="font-size: 12px; color: #64748b;">AI-generated compliance guidance — not legal advice. Always verify with a qualified professional. For questions, contact QuantumAIShield support.</p>
      </div>
    `;

    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.left = '-9999px';
    container.style.top = '0';
    container.style.width = '800px';
    container.style.backgroundColor = 'white';
    container.style.zIndex = '-1';
    container.innerHTML = htmlContent;
    document.body.appendChild(container);

    try {
      await new Promise(resolve => setTimeout(resolve, 300));
      const canvas = await html2canvas(container, { scale: 2, backgroundColor: '#ffffff', logging: false, useCORS: false });

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth  = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const HEADER_HEIGHT = 28;
      const FOOTER_HEIGHT = 22;
      const SIDE_MARGIN   = 14; // mm — page-level left/right margin (was 0, edge-to-edge)

      const usableWidth  = pdfWidth - 2 * SIDE_MARGIN;
      const usableHeight = pdfHeight - HEADER_HEIGHT - FOOTER_HEIGHT;
      const imgWidth     = usableWidth;
      const imgHeight    = (canvas.height * imgWidth) / canvas.width;

      // Smart slicing: detect blank rows for clean page breaks (same technique
      // as CrossFrameworkMapper.jsx's downloadDocument).
      const canvasCtx = canvas.getContext('2d');
      const imageData = canvasCtx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      const cw = canvas.width;

      const isRowBlank = (y) => {
        let darkPixels = 0;
        for (let x = 0; x < cw; x += 4) {
          const i = (y * cw + x) * 4;
          if (data[i] < 100 || data[i + 1] < 100 || data[i + 2] < 100) {
            darkPixels++;
            if (darkPixels > 2) return false;
          }
        }
        return true;
      };

      const pxPerMm = canvas.height / imgHeight;
      const usableHeightPx = Math.floor(usableHeight * pxPerMm);

      const blankRows = [];
      for (let y = 0; y < canvas.height; y += 2) {
        if (isRowBlank(y)) blankRows.push(y);
      }

      const pageBreaks = [0];
      let currentY = 0;
      while (currentY < canvas.height) {
        const target = currentY + usableHeightPx;
        if (target >= canvas.height) break;
        let breakAt = target;
        const minAllowed = currentY + usableHeightPx * 0.35;
        for (let i = blankRows.length - 1; i >= 0; i--) {
          if (blankRows[i] <= target && blankRows[i] > minAllowed) {
            breakAt = blankRows[i];
            break;
          }
        }
        pageBreaks.push(breakAt);
        currentY = breakAt;
      }

      // Drop trailing blank pages
      let lastContentRow = 0;
      for (let y = canvas.height - 1; y >= 0; y -= 2) {
        if (!isRowBlank(y)) { lastContentRow = y; break; }
      }
      while (pageBreaks.length > 1 && pageBreaks[pageBreaks.length - 1] >= lastContentRow - 20) {
        pageBreaks.pop();
      }
      const totalPages = pageBreaks.length;

      const drawHeaderFooter = (pageNum) => {
        if (pageNum > 1) {
          pdf.setFontSize(11);
          pdf.setTextColor(80, 80, 80);
          const logoW  = logoBase64 ? 10 : 0;
          const gap    = logoBase64 ? 3  : 0;
          const titleW = pdf.getTextWidth(headerTitle);
          const groupW = logoW + gap + titleW;
          const groupStartX = (pdfWidth - groupW) / 2;
          if (logoBase64) { try { pdf.addImage(logoBase64, 'PNG', groupStartX, 6, 10, 10); } catch {} }
          pdf.text(headerTitle, groupStartX + logoW + gap, 13);
          pdf.setDrawColor(200, 200, 200);
          pdf.setLineWidth(0.2);
          pdf.line(SIDE_MARGIN + 4, HEADER_HEIGHT - 4, pdfWidth - SIDE_MARGIN - 4, HEADER_HEIGHT - 4);
        }
        pdf.setFontSize(9);
        pdf.setTextColor(120, 120, 120);
        pdf.line(SIDE_MARGIN + 4, pdfHeight - FOOTER_HEIGHT + 6, pdfWidth - SIDE_MARGIN - 4, pdfHeight - FOOTER_HEIGHT + 6);
        pdf.text('www.QuantumAIShield.com',          SIDE_MARGIN + 4,            pdfHeight - 8);
        pdf.text(`Page ${pageNum} of ${totalPages}`, pdfWidth / 2,               pdfHeight - 8, { align: 'center' });
        pdf.text(new Date().toLocaleDateString(),    pdfWidth - SIDE_MARGIN - 4, pdfHeight - 8, { align: 'right' });
      };

      for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
        if (pageNum > 1) pdf.addPage();
        const startPx = pageBreaks[pageNum - 1];
        const endPx   = pageNum < totalPages ? pageBreaks[pageNum] : canvas.height;
        const slicePx = endPx - startPx;
        const sliceMm = slicePx / pxPerMm;

        const tempCanvas = document.createElement('canvas');
        tempCanvas.width  = canvas.width;
        tempCanvas.height = slicePx;
        const tempCtx = tempCanvas.getContext('2d');
        tempCtx.fillStyle = '#ffffff';
        tempCtx.fillRect(0, 0, canvas.width, slicePx);
        tempCtx.drawImage(canvas, 0, startPx, canvas.width, slicePx, 0, 0, canvas.width, slicePx);
        const sliceData = tempCanvas.toDataURL('image/png');

        pdf.addImage(sliceData, 'PNG', SIDE_MARGIN, HEADER_HEIGHT, imgWidth, sliceMm);
        pdf.setFillColor(255, 255, 255);
        pdf.rect(0, 0, pdfWidth, HEADER_HEIGHT, 'F');
        pdf.rect(0, pdfHeight - FOOTER_HEIGHT, pdfWidth, FOOTER_HEIGHT, 'F');
        drawHeaderFooter(pageNum);
      }

      const safeTitle = sessionTitle.replace(/[^a-zA-Z0-9]+/g, '_').slice(0, 40);
      pdf.save(`QuantumAIShield_AIAssistant_${safeTitle}_${companyName || 'AI'}_${Date.now()}.pdf`);
    } catch (err) {
      console.error('AI Assistant PDF generation error:', err);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      document.body.removeChild(container);
      setPdfGenerating(false);
    }
  };

  // Group sessions by date
  const grouped = sessions.reduce((acc, s) => {
    const label = formatTime(s.updatedAt || s.createdAt);
    if (!acc[label]) acc[label] = [];
    acc[label].push(s);
    return acc;
  }, {});

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 80px)', backgroundColor: '#f8fafc', overflow: 'hidden' }}>

      {/* ── LEFT SIDEBAR ── */}
      <div style={{
        width: sidebarOpen ? '260px' : '0px', minWidth: sidebarOpen ? '260px' : '0px',
        backgroundColor: '#0f172a', display: 'flex', flexDirection: 'column',
        transition: 'all 0.25s ease', overflow: 'hidden', flexShrink: 0,
      }}>
        {sidebarOpen && (
          <>
            {/* New chat button */}
            <div style={{ padding: '16px 12px', borderBottom: '1px solid #1e293b' }}>
              <button onClick={startNewChat} style={{
                width: '100%', padding: '10px 14px', backgroundColor: '#1e293b',
                color: 'white', border: '1px solid #334155', borderRadius: '8px',
                cursor: 'pointer', fontSize: '14px', fontWeight: '600',
                display: 'flex', alignItems: 'center', gap: '8px', transition: 'background 0.15s'
              }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#2d3f55'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = '#1e293b'}
              >
                <span style={{ fontSize: '16px' }}>✏️</span> New Conversation
              </button>
            </div>

            {/* Session list */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
              {sessions.length === 0 && (
             <p style={{ fontSize: '16px', color: '#14B8A6', padding: '16px', textAlign: 'center' }}>
                  No conversations yet. Start chatting!
                </p>
              )}
              {Object.entries(grouped).map(([label, group]) => (
                <div key={label}>
                  <p style={{ fontSize: '11px', color: '#cbd5e1', padding: '8px 14px 4px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {label}
                  </p>
                  {group.map(s => (
                    <div key={s.id} onClick={() => openSession(s.id)} style={{
                      padding: '8px 12px', cursor: 'pointer', borderRadius: '6px', margin: '1px 6px',
                      backgroundColor: activeId === s.id ? '#1e40af' : 'transparent',
                      display: 'flex', alignItems: 'center', gap: '6px', group: 'session',
                    }}
                      onMouseEnter={e => { if (activeId !== s.id) e.currentTarget.style.backgroundColor = '#1e293b'; }}
                      onMouseLeave={e => { if (activeId !== s.id) e.currentTarget.style.backgroundColor = 'transparent'; }}
                    >
                      <span style={{ fontSize: '13px', flexShrink: 0 }}>💬</span>
                      {editingId === s.id ? (
                        <input
                          value={editTitle}
                          onChange={e => setEditTitle(e.target.value)}
                          onBlur={() => saveRename(s.id)}
                          onKeyDown={e => e.key === 'Enter' && saveRename(s.id)}
                          onClick={e => e.stopPropagation()}
                          autoFocus
                          style={{ flex: 1, fontSize: '12px', backgroundColor: '#334155', color: 'white', border: '1px solid #3b82f6', borderRadius: '4px', padding: '2px 6px', outline: 'none' }}
                        />
                      ) : (
                        <span style={{ flex: 1, fontSize: '12px', color: activeId === s.id ? 'white' : '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {s.title}
                        </span>
                      )}
                      <div style={{ display: 'flex', gap: '2px', flexShrink: 0, opacity: activeId === s.id ? 1 : 0 }}
                        onMouseEnter={e => { e.currentTarget.style.opacity = '1'; e.stopPropagation(); }}
                      >
                        <button onClick={e => startRename(s.id, s.title, e)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '2px', fontSize: '11px' }} title="Rename">✏️</button>
                        <button onClick={e => deleteSession(s.id, e)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '2px', fontSize: '11px' }} title="Delete">🗑️</button>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>

            {/* Footer */}
            <div style={{ padding: '12px', borderTop: '1px solid #1e293b' }}>
              <p style={{ fontSize: '14px', color: '#14B8A6', padding: '16px', textAlign: 'center' }}>
                🛡️ Quantum AI Shield - AI Assistant<br />
                <span style={{ color: '#14B8A6' }}>EU AI Act · ISO 42001 · NIST RMF</span>
              </p>
            </div>
          </>
        )}
      </div>

      {/* ── MAIN CHAT AREA ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* Chat header */}
        <div style={{ padding: '12px 20px', backgroundColor: 'white', borderBottom: '2px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
          <button onClick={() => setSidebarOpen(p => !p)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', padding: '4px', color: '#64748b' }} title={sidebarOpen ? 'Hide history' : 'Show history'}>
            {sidebarOpen ? '◀' : '▶'}
          </button>
          <div>
            <h2 style={{ fontSize: '27px', fontWeight: 'bold', color: '#0f172a', margin: 0 }}>
              🤖 AI GRC Assistant - Ask Your Question
            </h2>
            <p style={{ fontSize: '22px', color: '#64748b', margin: 0 }}>
              {activeSession ? activeSession.title : 'I can help anything about EU AI Act, ISO 42001, or NIST AI RMF'}
            </p>
          </div>
          {activeId && (
            <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
              {messages.length > 0 && (
                <button onClick={downloadChatAsPDF} disabled={pdfGenerating} style={{
                  fontSize: '13px', padding: '6px 12px',
                  backgroundColor: pdfGenerating ? '#cbd5e1' : '#0e7c7c',
                  color: 'white', border: 'none', borderRadius: '8px',
                  cursor: pdfGenerating ? 'wait' : 'pointer', fontWeight: '600',
                  display: 'flex', alignItems: 'center', gap: '6px',
                }}>
                  {pdfGenerating ? <><span>⏳</span><span>Generating PDF...</span></> : <><span>⬇️</span><span>Download as PDF</span></>}
                </button>
              )}
              <button onClick={startNewChat} style={{ fontSize: '13px', padding: '6px 12px', backgroundColor: '#f1f5f9', color: '#334155', border: '2px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>
                ✏️ New Chat
              </button>
            </div>
          )}
        </div>

        {/* Messages */}
      <div style={{
          flex: messages.length === 0 ? '0 0 auto' : 1,
          overflowY: 'auto',
          padding: '24px 20px'
        }}>

        {messages.length === 0 && (
            <div style={{ maxWidth: '700px', margin: '0 auto', textAlign: 'center' }}>

              <p style={{ fontSize: '14px', fontWeight: '600', color: '#0e7c7c', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                ✨ Suggested Questions
              </p>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '10px', textAlign: 'left' }}>


                  {STARTERS.map((s, i) => (
                  <div key={i} onClick={() => sendMessage(s)} style={{
                    padding: '12px 16px',
                    backgroundColor: '#f0fdfa',
                    border: '2px solid #0e7c7c',
                    borderRadius: '10px', cursor: 'pointer', fontSize: '13px', color: '#0f172a', lineHeight: '1.5',
                    fontWeight: '500',
                    transition: 'all 0.15s ease',
                  }}
                    onMouseEnter={e => {
                      e.currentTarget.style.backgroundColor = '#ccfbf1';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(14, 124, 124, 0.2)';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.backgroundColor = '#f0fdfa';
                      e.currentTarget.style.boxShadow = 'none';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    {s}
                  </div>
                ))}


              </div>
            </div>
          )}

          {messages.map((msg, i) => (
            <div key={i} style={{ display: 'flex', gap: '12px', marginBottom: '20px', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '900px', margin: '0 auto 20px' }}>
              {msg.role === 'assistant' && (
                <div style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '4px' }}>
                  <span style={{ fontSize: '16px' }}>🤖</span>
                </div>
              )}
              <div style={{
                maxWidth: '75%', padding: '12px 16px', borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                backgroundColor: msg.role === 'user' ? '#1e40af' : 'white',
                color: msg.role === 'user' ? 'white' : '#0f172a',
                border: msg.role === 'user' ? 'none' : '2px solid #e2e8f0',
                fontSize: '14px', lineHeight: '1.7',
                whiteSpace: msg.role === 'user' ? 'pre-wrap' : 'normal',
              }}>
                {msg.role === 'assistant' ? (
                  <MarkdownMessage content={msg.content} />
                ) : (
                  msg.content
                )}
                <p style={{ fontSize: '10px', color: msg.role === 'user' ? 'rgba(255,255,255,0.6)' : '#94a3b8', margin: '6px 0 0', textAlign: 'right' }}>
                  {new Date(msg.ts).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
              {msg.role === 'user' && (
                <div style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '4px' }}>
                  <span style={{ fontSize: '14px', color: 'white', fontWeight: 'bold' }}>U</span>
                </div>
              )}
            </div>
          ))}

          {loading && (
            <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', maxWidth: '900px', margin: '0 auto 20px' }}>
              <div style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <span style={{ fontSize: '16px' }}>🤖</span>
              </div>
              <div style={{ padding: '14px 18px', backgroundColor: 'white', border: '2px solid #e2e8f0', borderRadius: '16px 16px 16px 4px', display: 'flex', gap: '6px', alignItems: 'center' }}>
                {[0, 1, 2].map(i => (
                  <div key={i} style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#94a3b8', animation: `bounce 1.2s infinite ${i * 0.2}s` }} />
                ))}
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input area */}
        <div style={{ padding: '16px 20px', backgroundColor: 'white', borderTop: '2px solid #e2e8f0', flexShrink: 0 }}>
          <div style={{ maxWidth: '900px', margin: '0 auto', display: 'flex', gap: '10px', alignItems: 'flex-end' }}>

            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
              onFocus={e => { e.target.style.borderColor = '#5eead4'; e.target.style.backgroundColor = '#f0fdfa'; e.target.style.boxShadow = '0 0 0 3px rgba(14, 124, 124, 0.15)'; }}
              onBlur={e => { e.target.style.borderColor = '#5eead4'; e.target.style.backgroundColor = '#f0fdfa'; e.target.style.boxShadow = 'none'; }}
              placeholder="💬 Ask your question about EU AI Act, ISO 42001, NIST AI RMF... (Enter to send)"
              rows={1}
              style={{
                flex: 1, padding: '14px 18px', fontSize: '18px',
                border: '4px solid #5eead4',
                backgroundColor: '#f0fdfa',
                borderRadius: '12px', outline: 'none', resize: 'none', fontFamily: 'inherit',
                lineHeight: '1.5', maxHeight: '120px', overflow: 'auto',
                transition: 'box-shadow 0.15s ease',
                fontWeight: '500',
                color: '#0f172a',
              }}
              onInput={e => { e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px'; }}
            />


            <button onClick={() => sendMessage()} disabled={!input.trim() || loading} style={{
              padding: '12px 24px', backgroundColor: !input.trim() || loading ? '#cbd5e1' : '#0e7c7c',
              color: 'white', border: 'none', borderRadius: '12px', cursor: !input.trim() || loading ? 'not-allowed' : 'pointer',
              fontSize: '15px', fontWeight: 'bold', flexShrink: 0, transition: 'background 0.15s',
              boxShadow: !input.trim() || loading ? 'none' : '0 2px 6px rgba(14, 124, 124, 0.3)',
            }}>

              {loading ? '⏳' : '➤'}
            </button>
          </div>
          <p style={{ fontSize: '15px', color: '#000000', textAlign: 'center', margin: '8px 0 0' }}>
           ⚠️ Disclaimer: AI‑generated compliance guidance – not legal advice. Always verify with a qualified professional. For questions, contact QuantumAIshield support. Blockchain records are permanent – please confirm before sealing.
          </p>
        </div>
      </div>

      <style>{`
        @keyframes bounce {
          0%, 60%, 100% { transform: translateY(0); }
          30% { transform: translateY(-6px); }
        }

        /* ── Markdown rendering for assistant messages ── */
        .md-content { font-size: 14px; line-height: 1.7; color: #0f172a; }
        .md-content > *:first-child { margin-top: 0; }
        .md-content > *:last-child { margin-bottom: 0; }
        .md-content p { margin: 0 0 10px; }
        .md-content strong { font-weight: 700; color: #0f172a; }
        .md-content em { font-style: italic; }
        .md-content ul, .md-content ol { margin: 0 0 10px; padding-left: 22px; }
        .md-content li { margin-bottom: 4px; }
        .md-content h1, .md-content h2, .md-content h3, .md-content h4 {
          font-weight: 700; color: #0e7c7c; margin: 14px 0 8px;
          line-height: 1.3;
        }
        .md-content h1:first-child, .md-content h2:first-child,
        .md-content h3:first-child, .md-content h4:first-child { margin-top: 0; }
        .md-content h1 { font-size: 18px; }
        .md-content h2 { font-size: 16px; }
        .md-content h3 { font-size: 15px; }
        .md-content h4 { font-size: 14px; }
        .md-content code {
          background: #f0fdfa; color: #0e7c7c; padding: 1px 5px;
          border-radius: 4px; font-size: 12.5px; font-family: 'SF Mono', Consolas, monospace;
        }
        .md-content pre {
          background: #0f172a; color: #e2e8f0; padding: 12px 14px;
          border-radius: 8px; overflow-x: auto; margin: 0 0 10px;
        }
        .md-content pre code { background: none; color: inherit; padding: 0; }
        .md-content blockquote {
          border-left: 3px solid #5eead4; margin: 0 0 10px; padding: 4px 12px;
          color: #475569; background: #f0fdfa;
        }
        .md-content a { color: #1e40af; text-decoration: underline; }
        .md-content hr { border: none; border-top: 1px solid #e2e8f0; margin: 14px 0; }

        .md-content table {
          border-collapse: collapse; width: 100%; margin: 0 0 14px;
          font-size: 13px; overflow-x: auto; display: block;
        }
        .md-content thead { background: #0e7c7c; }
        .md-content th {
          color: white; text-align: left; padding: 8px 12px;
          font-weight: 600; border: 1px solid #0e7c7c;
        }
        .md-content td {
          padding: 8px 12px; border: 1px solid #e2e8f0;
          vertical-align: top;
        }
        .md-content tbody tr:nth-child(even) { background: #f8fafc; }
        .md-content tbody tr:hover { background: #f0fdfa; }
      `}</style>

    </div>
  );
}